import { computed } from '@angular/core';
import {
  signalStoreFeature,
  type,
  withComputed,
  withMethods,
} from '@ngrx/signals';
import { IndicatorDataState } from './indicator-data.state';
import { IndicatorData } from '../../models/indicator-data';
import { Indicator } from '../../models/indicator';
import { StateFilter } from '../crud.state';
import { makeUniqueStringArray } from '../../utility/array.utility';
import { groupByType } from '../data-layer/data-layer.selectors';

/**
 * Filters a list of IndicatorData based on the provided StateFilter criteria.
 *
 * @param {IndicatorData[]} items - The array of items to filter.
 * @param {StateFilter} filter - The filter criteria. Contains properties to match against the items.
 * @returns {IndicatorData[]} A filtered array of items that meet the specified filter criteria.
 */
const filterItems = (
  items: IndicatorData[],
  filter: StateFilter
): IndicatorData[] => {
  return items.filter(item => {
    const publishMatch =
      filter.published === item.published || filter.published === null;

    return publishMatch;
  });
};

/**
 * Groups an array of IndicatorData objects by their type and layer.
 *
 * @param {IndicatorData[]} data - The array of IndicatorData objects to be grouped.
 * @return {Record<string, Record<string, IndicatorData>>} A nested object where the first level keys represent data layer types,
 * and the second level keys represent data layers, with each value being the corresponding IndicatorData object.
 */
function groupDataByTypeAndLayer(
  data: IndicatorData[]
): Record<string, Record<string, IndicatorData>> {
  return data.reduce(
    (acc, entity) => {
      if (!acc[entity.dataLayerType]) {
        acc[entity.dataLayerType] = {};
      }
      acc[entity.dataLayerType][entity.dataLayer] = entity;
      return acc;
    },
    {} as Record<string, Record<string, IndicatorData>>
  );
}

/**
 * Groups an array of data by its `dataLayerType` and `indicator`.
 *
 * @param {IndicatorData[]} data - An array of objects containing `dataLayerType` and `indicator` properties.
 * @return {Record<string, Record<string, IndicatorData>>} Returns a nested object where the first level keys are `dataLayerType`
 * and the second level keys are `indicator`. The values are the data objects matching those keys.
 */
function groupDataByTypeAndIndicator(
  data: IndicatorData[]
): Record<string, Record<string, IndicatorData>> {
  return data.reduce(
    (acc, entity) => {
      if (!acc[entity.dataLayerType]) {
        acc[entity.dataLayerType] = {};
      }
      acc[entity.dataLayerType][entity.indicator] = entity;
      return acc;
    },
    {} as Record<string, Record<string, IndicatorData>>
  );
}

export function withIndicatorDataSelectors() {
  return signalStoreFeature(
    {
      state: type<IndicatorDataState>(),
    },
    withComputed(({ items, filter }) => ({
      count: computed(() => Object.keys(items()).length),
      asArray: computed(() => Object.values(items())),
      filteredItems: computed(() => {
        const all = items();
        let filtered = Object.values(all);
        if (filter()) {
          filtered = filterItems(filtered, filter());
        }
        return filtered as IndicatorData[];
      }),
      groupByIndicatorAndLayer: computed(() => {
        const all = items();
        let filtered = Object.values(all);
        if (filter()) {
          filtered = filterItems(filtered, filter());
        }
        return filtered.reduce(
          (acc, entity) => {
            if (!acc[entity.indicator]) {
              acc[entity.indicator] = {};
            }
            acc[entity.indicator][entity.dataLayer] = entity;
            return acc;
          },
          {} as Record<string, Record<string, IndicatorData>>
        );
      }),
      groupByLayerAndIndicator: computed(() => {
        const all = items();
        let filtered = Object.values(all);
        if (filter()) {
          filtered = filterItems(filtered, filter());
        }
        return filtered.reduce(
          (acc, entity) => {
            if (!acc[entity.dataLayer]) {
              acc[entity.dataLayer] = {};
            }
            acc[entity.dataLayer][entity.indicator] = entity;
            return acc;
          },
          {} as Record<string, Record<string, IndicatorData>>
        );
      }),
      groupByTypeAndLayer: computed(() => {
        return groupDataByTypeAndLayer(Object.values(items()));
      }),
      groupByIndicatorTypeAndLayer: computed(() => {
        return Object.values(items()).reduce(
          (acc, entity) => {
            if (!acc[entity.indicator]) {
              acc[entity.indicator] = {};
            }
            if (!acc[entity.indicator][entity.dataLayerType]) {
              acc[entity.indicator][entity.dataLayerType] = {};
            }
            acc[entity.indicator][entity.dataLayerType][entity.dataLayer] =
              entity;
            return acc;
          },
          {} as Record<string, Record<string, Record<string, IndicatorData>>>
        );
      }),
    })),
    withMethods(
      ({
        asArray,
        groupByLayerAndIndicator,
        groupByTypeAndLayer,
        groupByIndicatorAndLayer,
        groupByIndicatorTypeAndLayer,
      }) => ({
        getByDataLayer(dataLayerId: string) {
          return groupByLayerAndIndicator()[dataLayerId];
        },
        getByIndicator(indicatorId: string) {
          return groupByIndicatorAndLayer()[indicatorId];
        },
        getTypeGroupedByIndicator(indicatorId: string) {
          return groupByIndicatorTypeAndLayer()[indicatorId];
        },
        dataLayerTypeHasData(typeId: string) {
          return !!groupByTypeAndLayer()[typeId];
        },
        dataLayerTypeAndIndicatorsHasData(
          typeId: string,
          indicatorIds: string[]
        ) {
          const dataByIndicatorIds = asArray().filter((item: IndicatorData) => {
            return indicatorIds.includes(item.indicator);
          });
          return !!groupDataByTypeAndIndicator(dataByIndicatorIds)[typeId];
        },
        getByDataLayerTypeAndIndicators(
          typeId: string,
          indicatorIds: string[]
        ) {
          const dataByIndicatorIds = asArray().filter((item: IndicatorData) => {
            return indicatorIds.includes(item.indicator);
          });
          return groupDataByTypeAndLayer(dataByIndicatorIds)[typeId];
        },
        dataLayerHasData(dataLayerId: string) {
          return !!groupByLayerAndIndicator()[dataLayerId];
        },
        dataLayerHasPublishedData(dataLayerId: string) {
          return (
            Object.values(groupByLayerAndIndicator()[dataLayerId]).length > 0
          );
        },
        dataLayerAndIndicatorHasData(
          dataLayerId: string,
          indicatorId: string
        ): boolean {
          return !!(
            groupByLayerAndIndicator()[dataLayerId] &&
            groupByLayerAndIndicator()[dataLayerId][indicatorId]
          );
        },
        dataLayerAndIndicatorsHasData(
          dataLayerId: string,
          indicatorIds: string[]
        ): boolean {
          const layerData = groupByLayerAndIndicator()[dataLayerId];

          if (!layerData) return false;

          for (const idx in indicatorIds) {
            const id = indicatorIds[idx];
            if (!layerData[id]) return false;
          }

          return true;
        },
        countByDataLayer(dataLayerId: string) {
          return Object.keys(groupByLayerAndIndicator()?.[dataLayerId] || [])
            .length as number;
        },
        countByDataLayerType(dataLayerTypeId: string) {
          return Object.keys(groupByTypeAndLayer()?.[dataLayerTypeId] || [])
            .length as number;
        },
        indicatorsHaveData(indicatorIds: string[]) {
          return indicatorIds.some(id => !!groupByIndicatorAndLayer()[id]);
        },
        dataLayersHaveData(layerIds: string[]) {
          return layerIds.some(id => !!groupByLayerAndIndicator()[id]);
        },
        indicatorHasData(indicatorId: string) {
          return !!groupByIndicatorAndLayer()[indicatorId];
        },
        countByIndicator(indicatorId: string) {
          return Object.keys(groupByIndicatorAndLayer()?.[indicatorId] || [])
            .length as number;
        },
        categoryHasData(indicatorId: string) {
          return !!groupByIndicatorAndLayer()?.[indicatorId];
        },
        filterByIndicator(indicatorId: string) {
          return asArray().filter(entity => {
            return entity.indicator === indicatorId;
          });
        },
        filterByDataLayerType(dataLayerTypeId: string) {
          return asArray().filter(entity => {
            return entity.dataLayerType === dataLayerTypeId;
          });
        },
        filterByDataLayerTypeAndIndicator(
          dataLayerTypeId: string,
          indicatorId: string
        ) {
          return asArray().filter(
            entity =>
              entity.dataLayerType === dataLayerTypeId &&
              entity.indicator === indicatorId
          );
        },
        getLayerIdsByDataLayerTypeAndIndicators(
          dataLayerTypeId: string,
          indicatorIds: string[]
        ) {
          const dataByType = asArray().filter(
            entity => entity.dataLayerType === dataLayerTypeId
          );

          const dataLayerIds = makeUniqueStringArray(
            dataByType.map(item => item.dataLayer)
          );

          return dataLayerIds.filter(layerId => {
            const layerData = dataByType.filter(
              item => item.dataLayer === layerId
            );
            const layerIndicators = makeUniqueStringArray(
              layerData.map(item => item.indicator)
            );
            return indicatorIds.every(id => layerIndicators.includes(id));
          });
        },
        getIndicatorIdsByDataLayerTypeAndIndicators(
          dataLayerTypeId: string,
          indicatorIds: string[]
        ) {
          const dataByType = asArray().filter(
            entity => entity.dataLayerType === dataLayerTypeId
          );

          const targetDataLayers = dataByType
            .filter(d => indicatorIds.includes(d.indicator))
            .reduce(
              (acc, d) => {
                if (!acc[d.indicator]) {
                  acc[d.indicator] = new Set();
                }
                acc[d.indicator].add(d.dataLayer);
                return acc;
              },
              {} as Record<string, Set<string>>
            );

          if (!targetDataLayers) return [];

          const commonDataLayers = Object.values(targetDataLayers).reduce(
            (common, layers) => {
              if (!common) return layers;
              return new Set([...common].filter(x => layers.has(x)));
            }
          );

          return [
            ...indicatorIds,
            ...makeUniqueStringArray(
              dataByType
                .filter(d => commonDataLayers.has(d.dataLayer))
                .map(d => d.indicator)
            ),
          ];
        },
        filterByDataLayer(dataLayerId: string) {
          return asArray().filter(entity => {
            return entity.dataLayer === dataLayerId;
          });
        },
        getByDataLayerAndIndicator(dataLayerId: string, indicatorId: string) {
          return (
            groupByLayerAndIndicator()?.[dataLayerId]?.[indicatorId] || null
          );
        },
        getGroupedByIndicators(indicatorIds: string[]) {
          const dataByIndicatorIds = asArray().filter((item: IndicatorData) => {
            return indicatorIds.includes(item.indicator);
          });

          return groupDataByTypeAndLayer(dataByIndicatorIds);
        },
        getGroupedByDataLayer(indicatorIds: string[]) {
          const dataByIndicatorIds = asArray().filter((item: IndicatorData) => {
            return indicatorIds.includes(item.indicator);
          });

          return groupDataByTypeAndIndicator(dataByIndicatorIds);
        },
        getMinValueByLayerAndIndicator(
          dataLayerId: string,
          indicatorId: string,
          filterKeys?: string[]
        ) {
          const data: IndicatorData =
            groupByLayerAndIndicator()?.[dataLayerId]?.[indicatorId] || null;
          if (data?.data) {
            const values = Object.entries(data.data)
              .filter((entry): entry is [string, number] => {
                const [key, value] = entry;
                return !filterKeys || !filterKeys.includes(key);
              })
              .map(([, value]) => Number(value));

            return values.length ? Math.min(...values) : null;
          }

          return null;
        },
        getMaxValueByLayerAndIndicator(
          dataLayerId: string,
          indicatorId: string,
          filterKeys?: string[]
        ) {
          const data: IndicatorData =
            groupByLayerAndIndicator()?.[dataLayerId]?.[indicatorId] || null;
          if (data?.data) {
            const values = Object.entries(data.data)
              .filter((entry): entry is [string, number] => {
                const [key, value] = entry;
                return !filterKeys || !filterKeys.includes(key);
              })
              .map(([, value]) => Number(value));

            return values.length ? Math.max(...values) : null;
          }

          return null;
        },
        getByDataLayerAndIndicators(
          dataLayerId: string,
          indicatorIds: string[]
        ): IndicatorData[] | null {
          const layerGrouped = groupByLayerAndIndicator()?.[dataLayerId];

          if (!layerGrouped) return null;

          const result: IndicatorData[] = [];
          indicatorIds.forEach(id => {
            const data = layerGrouped[id];
            if (data) {
              result.push(data);
            }
          });
          return result.length ? result : null;
        },
        getByDataLayersAndIndicators(
          dataLayerIds: string[],
          indicatorIds: string[]
        ): IndicatorData[] | null {
          const data: IndicatorData[] = asArray();
          const result: IndicatorData[] = data.filter((item: IndicatorData) => {
            return (
              indicatorIds.includes(item.indicator) &&
              dataLayerIds.includes(item.dataLayer)
            );
          });
          return result.length ? result : null;
        },
        getIndicatorIdsWithData() {
          const data = asArray();
          return makeUniqueStringArray(data.map(data => data.indicator));
        },
        isPublished(dataLayerId: string, indicatorId: string): null | boolean {
          return !!(
            groupByLayerAndIndicator()[dataLayerId] &&
            groupByLayerAndIndicator()[dataLayerId][indicatorId] &&
            groupByLayerAndIndicator()[dataLayerId][indicatorId].published
          );
        },
        publishedByDataLayer(dataLayerId: string) {
          return asArray().filter(entity => {
            return entity.dataLayer === dataLayerId && entity.published;
          });
        },
        lockedByDataLayer(dataLayerId: string) {
          return asArray().filter(entity => {
            return entity.dataLayer === dataLayerId && !entity.published;
          });
        },
      })
    )
  );
}
